import h from 'hyperscript';

import publishedIcon from '@disy/cadenza-icons/pub-state-up-to-date.svg';
import { on } from '@disy/lib/event-util';
import { applyIconSize, icon } from '@disy/lib/icon';
import { logger } from '@disy/lib/logging';
import { showToastMessage } from '@disy/ui/toast-message';
import type { ToolbarItem } from '@disy/ui/toolbar';

import type { Modal } from 'ui/dialog/modal/modal';

import { PublishObjecttypeValidationDialog } from 'cadenza/admin/publish-objecttype-dialog/publish-objecttype-validation-dialog';
import { renderPublicationState } from 'cadenza/admin/ui/publication-state-icon/publication-state-icon';
import { errorAlert } from 'cadenza/alert';
import { BackendAbortController } from 'cadenza/api-client/abort-controller/backend-abort-controller';
import { ajax } from 'cadenza/api-client/api';
import type { ObjecttypePublishingNetworkDto } from 'cadenza/api-client/dto/objecttype-publishing-network-dto';
import type { PublicationState } from 'cadenza/api-client/dto/publication-state';
import type { WorkOrderProgressEvent } from 'cadenza/api-client/dto/workorder-dto';
import { type WorkOrderId, deleteWorkOrders, observeForResult } from 'cadenza/api-client/workorder-api';
import { MIMETYPE_JSON } from 'cadenza/model/media-type';
import type { ObjecttypeId, ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import { asObjecttypeIdString, objecttypeIdToString } from 'cadenza/repository/model/objecttype-id.gen';
import type { WorkbookName } from 'cadenza/repository/model/workbook-name.gen';
import { createAndConnectLoadingDialog } from 'cadenza/task-list-dialog/create-task-list-dialog';
import { runTestsuiteForWorkbooksForObjecttype } from 'cadenza/testsuite/api/testsuite-api';
import type { ValidationResultEvent } from 'cadenza/testsuite/types';

import i18n from './publish-objecttype-actions.properties';

export function createPublishAction(
  objecttypeId: ObjecttypeId,
  publicationState: PublicationState,
  publishedCallback: () => void,
): ToolbarItem {
  let objecttypePublishingNetworkDto: ObjecttypePublishingNetworkDto;
  return {
    label: i18n('publish.button.title'),
    icon: icon(publishedIcon),
    disabled: publicationState === 'PUBLISHED',
    execute: () =>
      import('cadenza/admin/publish-objecttype-dialog/publish-objecttype-dialog')
        .then(async ({ openPublishDialog }) => {
          objecttypePublishingNetworkDto = await getObjecttypePublishingNetworkDto(asObjecttypeIdString(objecttypeId));
          return openPublishDialog(objecttypePublishingNetworkDto);
        })
        .then((dialogSubmitted) =>
          handlePublication(dialogSubmitted, objecttypePublishingNetworkDto, publishedCallback),
        ),
  };
}

function getObjecttypePublishingNetworkDto(id: ObjecttypeIdString) {
  return ajax<ObjecttypePublishingNetworkDto>(`/objecttypes/${btoa(id)}/publish-network`, {
    dataType: 'json',
  });
}

async function handlePublication(
  dialogSubmitted: boolean,
  objecttypePublishingNetworkDto: ObjecttypePublishingNetworkDto,
  publishedCallback: () => void,
) {
  if (!dialogSubmitted) {
    return;
  }

  let loadingMessageDialog = createAndConnectLoadingDialog();
  loadingMessageDialog.showCancelButton(i18n('testing.skipTests'));

  try {
    const validationResult = await loadingMessageDialog.addTask({
      promise: runValidation(objecttypePublishingNetworkDto, loadingMessageDialog, publishedCallback),
      title: i18n('testing.loadingResults'),
    });

    if (validationResult) {
      await showWarningOrPublishObjecttype(
        objecttypePublishingNetworkDto,
        loadingMessageDialog,
        publishedCallback,
        validationResult,
      );
    }
  } catch (error) {
    // if loading dialog is closed on finally, it is still seen for a second after closing the error alert dialog, so we need to close it before
    loadingMessageDialog.close();
    await showPublishObjecttypeErrorAlert(error);
  }
}

function runValidation(
  objecttypePublishingNetworkDto: ObjecttypePublishingNetworkDto,
  loadingMessageDialog: Modal,
  publishedCallback: () => void,
): Promise<ValidationResultEvent | undefined> {
  const abortController = new BackendAbortController();
  let currentWorkOrderId: WorkOrderId | null;
  loadingMessageDialog.addEventListener('cancel', () => {
    deleteWorkOrders([currentWorkOrderId!]);
    currentWorkOrderId = null;
    publishObjecttype(objecttypePublishingNetworkDto, publishedCallback);
  });
  let workbookNames: WorkbookName[] = [];
  objecttypePublishingNetworkDto.workbooksInNetwork.forEach((x) => {
    workbookNames.push(x.workbookName);
  });
  if (workbookNames.length === 0) {
    return Promise.resolve(undefined);
  }
  return runTestsuiteForWorkbooksForObjecttype(
    workbookNames,
    'PUBLISHED',
    objecttypePublishingNetworkDto.printableObjecttypeDto.objecttypeId,
    abortController.signal,
  )
    .then((workOrderId) => {
      currentWorkOrderId = workOrderId;
      /* delete workorder on leaving the page */
      window.addEventListener('beforeunload', () => deleteWorkOrders([currentWorkOrderId!]));
      return observeForResult<unknown, WorkOrderProgressEvent, ValidationResultEvent>(
        currentWorkOrderId,
        'workbook-validation-result',
      );
    })
    .then((validationResult) => {
      if (!currentWorkOrderId) {
        // avoids continuing testing after user skips it on slow internet connections
        return;
      }
      return validationResult;
    })
    .catch((error) => {
      deleteWorkOrders([currentWorkOrderId!]);
      logger.error(error);
      throw error;
    });
}

function showWarningOrPublishObjecttype(
  objecttypePublishingNetworkDto: ObjecttypePublishingNetworkDto,
  loadingMessageDialog: Modal,
  publishedCallback: () => void,
  validationResult?: ValidationResultEvent,
) {
  if (validationResult && (validationResult.results.warnings > 0 || validationResult.results.failures > 0)) {
    const testSuiteResultId = validationResult.workOrderId;

    // Show warning dialog
    const validationDialog = new PublishObjecttypeValidationDialog({
      numberOfErrors: validationResult.results.failures,
      numberOfWarnings: validationResult.results.warnings,
      loadingMessageDialog,
      objecttypePublishingNetworkDto,
      testSuiteResultId,
      publish: () => publishObjecttype(objecttypePublishingNetworkDto, publishedCallback),
    });
    loadingMessageDialog.classList.remove('visible-footer');

    on(window, 'beforeunload', () => deleteWorkOrders([testSuiteResultId]));
    on(validationDialog, 'close', () => deleteWorkOrders([testSuiteResultId]));

    return validationDialog.connectAndShow();
  } else {
    loadingMessageDialog.closeAll();
    return publishObjecttype(objecttypePublishingNetworkDto, publishedCallback);
  }
}

async function publishObjecttype(
  objecttypePublishingNetworkDto: ObjecttypePublishingNetworkDto,
  publishedCallback: () => void,
) {
  try {
    const objecttypeId = objecttypePublishingNetworkDto.printableObjecttypeDto.objecttypeId;
    await ajax(`/objecttypes/${btoa(objecttypeIdToString(objecttypeId))}/published`, {
      type: 'PUT',
      contentType: MIMETYPE_JSON,
    });
    showObjecttypePublishedToast();
    publishedCallback();
  } catch (error) {
    await showPublishObjecttypeErrorAlert(error);
  }
}

function showObjecttypePublishedToast() {
  return showToastMessage(i18n('publish-objecttype.published.finished'), { type: 'success' });
}

function showPublishObjecttypeErrorAlert(err: unknown) {
  logger.error('Error when trying to publish objecttype ', err);
  return errorAlert(i18n('error.publish.title'), i18n('error.publish.message'));
}

export function createPublicationLabel(publicationState: PublicationState) {
  return h('.d-stack-h.space-1', [
    applyIconSize(renderPublicationState(publicationState), 'xs'),
    i18n(`publication-state.<${publicationState}>`),
  ]);
}
